import { IStateDataSpeciesList } from '../../../../../FindUSSpecies/helpers/buildStateDataSpecies';
import { getRegionUrl, getSpeciesGroupRegionUrl } from '@domain/routes/urls';
import { InternalLink } from '../../../../../Clickables/InternalLink/InternalLink';

interface IProps {
  testId?: string;
  topSpeciesData?: IStateDataSpeciesList[];
  mode: 'states' | 'speciesGroupings';
  countryId: string;
  countryName: string;
  speciesGroupingExternalId?: string;
}

/**
 * Component that renders a list of SEO-friendly internal links.
 * The links are hidden from the user interface but can be used by search engines for SEO purposes.
 *
 * Modes:
 * - 'states': Creates links to each state/region
 * - 'speciesGroupings': Creates links to species groupings in each state
 *   - When speciesGroupingExternalId is provided: Only links for that specific grouping
 *   - When speciesGroupingExternalId is not provided: Links for all species groupings
 */
export const MapSEOLinks = ({
  testId = 'MapSEOLinks',
  topSpeciesData,
  mode,
  countryId,
  countryName,
  speciesGroupingExternalId,
}: IProps) => {
  if (!topSpeciesData || topSpeciesData.length === 0) {
    return null;
  }

  const renderLinks = () => {
    switch (mode) {
      case 'states':
        // Create one link per state to the region page
        return topSpeciesData.map(({ stateId, stateName }) => (
          <InternalLink key={stateId} to={getRegionUrl(countryId, stateId, countryName, stateName)}>
            {stateName}
          </InternalLink>
        ));

      case 'speciesGroupings':
        // Create links for species groupings in each state
        // Filter by specific grouping if speciesGroupingExternalId is provided
        return topSpeciesData.flatMap(({ stateId, stateName, speciesGroupings }) => {
          const filteredGroupings = speciesGroupingExternalId
            ? speciesGroupings.filter(({ externalId }) => externalId === speciesGroupingExternalId)
            : speciesGroupings;

          return filteredGroupings.map(({ externalId, name }) => (
            <InternalLink
              key={`${stateId}-${externalId}`}
              to={getSpeciesGroupRegionUrl(countryId, stateId, externalId)}
            >
              {name} in {stateName}
            </InternalLink>
          ));
        });

      default:
        return null;
    }
  };

  return (
    <div data-testid={testId} className="hidden">
      {renderLinks()}
    </div>
  );
};
